import { useCallback, useEffect, useState } from 'react';

import { TARGET_LEXILE, LANG_EN, NUMERICAL_GRADE_BANDS } from '@client/utils/constants';
import { fetchJSON } from '@client/utils/rest';
import { error, warn } from '@client/utils/toast';

import { $levelerWarningCss } from '../style';

export const CONTENT_FILTER_STOP = 'CONTENT_FILTER_STOP';
export const CONTENT_FILTERED_MESSAGE = 'Content filtered due to sensitive content';

function findHighestGradeBand (obj) {
  const gradeOrder = {
    GRADE_K: 0, // Assign a numeric value to K for comparison purposes
    ...NUMERICAL_GRADE_BANDS
  };

  return obj?.articleLevels?.reduce((highest, level) => {
    const highestValue = gradeOrder[highest];
    const currentValue = gradeOrder[level.gradeBand];
    return currentValue > highestValue ? level.gradeBand : highest;
  }, 'GRADE_K');
}
function shouldDisableLeveler ({ language, parent, gradeBand, isOriginalLevel, levelAttempts, text }) {
  const MAX_LEVEL_ATTEMPTS = 3;
  const reasons = [];

  if (language !== LANG_EN) {
    reasons.push('Leveling is only available for English texts.');
  }

  if (!text || text.length < 2) {
    reasons.push('Text is too short to be leveled. Provide a longer text.');
  }

  if (levelAttempts >= MAX_LEVEL_ATTEMPTS) {
    reasons.push(`Maximum leveling attempts (${MAX_LEVEL_ATTEMPTS}) reached.`);
  }

  const highestGradeBandSupported = findHighestGradeBand(parent);
  const isCurrentGradeHighest = highestGradeBandSupported === gradeBand;
  if (isCurrentGradeHighest) {
    reasons.push('Text is already at the highest grade band.');
  }

  if (isOriginalLevel || gradeBand === 'GRADE_12') {
    reasons.push('Original-level or Grade 12 texts cannot be leveled.');
  }

  return {
    isDisabled: reasons.length > 0,
    reasons,
  };
}

/**
 * Extracts not-leveled data from the API response.
 * @param {Array} sections - Sections from the API response.
 * @returns {Array} List of not-leveled data with reasons and content.
 */
const collectNotLeveledData = (sections) => {
  const notLeveledData = [];

  for (const section of sections) {
    const leveledVersions = section.leveled_versions;

    for (const leveledVersion of leveledVersions) {
      if (leveledVersion.not_leveled_reason === CONTENT_FILTER_STOP) {
        notLeveledData.push({
          reason: CONTENT_FILTERED_MESSAGE,
          content: leveledVersion.raw_text?.content || [],
        });
      }
    }
  }

  return notLeveledData;
};

/**
 * Formats the warning toast content.
 * @param {Array} notLeveledData - Data to display in the toast.
 * @returns {JSX.Element} The JSX content for the toast.
 */
const notLeveledToastWarning = (notLeveledData) => (
  <div css={$levelerWarningCss}>
    <p>The following sections have not been leveled</p>
    <ul>
      {notLeveledData.map((item, index) => (
        <li key={index}>
          <p>
            <strong>Reason:</strong> {item.reason}
          </p>
          {item.content.length > 0 && (
            <div>
              <strong>Section:</strong>
              {item.content.map((paragraph, i) => (
                <section key={i}>
                  {paragraph.content?.map((node) => node.text).join(' ') || ''}
                </section>
              ))}
            </div>
          )}
        </li>
      ))}
    </ul>
  </div>
);

const useLeveler = ({ gradeBand, rawText, text, isOriginalLevel, language, __typename }, parent) => {
// Only article-level schemas can be leveled, as we're currently leveling articles only.
  const isLevelerAllowed = __typename === 'ArticleLevel';

  const [levelerStatus, setLevelerStatus] = useState({
    levelerDisabled: shouldDisableLeveler({
      language,
      parent,
      gradeBand,
      isOriginalLevel
    }),
    isToggleLevelerDisabled: true,
    toggleLeveledText: false,
    leveledText: null,
    levelAttempts: 0
  });

  const [leveledData, setLeveledData] = useState({
    data: null,
    loading: false,
    error: null,
  });

  const fetchLeveledArticle = useCallback(async () => {
    setLevelerStatus((prevState) => {
      return {
        ...prevState,
        isToggleLevelerDisabled: false,
        toggleLeveledText: true,
        leveledGradeBand: gradeBand
      };
    });
    setLevelerStatus((prev) => {
      if (prev.leveledText === text) {
        return { ...prev, leveledText: text, levelAttempts: prev.levelAttempts + 1 };
      } else {
        return { ...prev, leveledText: text, levelAttempts: 0 };
      }
    });

    setLeveledData({ data: null, loading: true, error: null });

    try {
      const body = JSON.stringify({
        original_text: { format: 'TEXT_RAW', raw_text: rawText },
        targets: [{ lexile: range }]
      });
      const options = {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body
      };
      const levelerURL = `${process.env.SETI_BASE_URL}/leveler/level-article`;
      const res = await fetchJSON(levelerURL, options);

      const sections = res.article_levels[0].sections;

      const notLeveledData = collectNotLeveledData(sections);

      if (notLeveledData.length > 0) {
        warn(notLeveledToastWarning(notLeveledData), { persist: true });
      }

      setLeveledData({ data: res, loading: false, error: null });
    } catch (err) {
      setLeveledData({ data: null, loading: false, error: 'An error happened while leveling. If the error persists, please reach out to #content-production.' });
      setLevelerStatus((prevState) => {
        const newState = {
          ...prevState,
          isToggleLevelerDisabled: true,
          toggleLeveledText: false,
        };

        return newState;
      });
    }
  }, [rawText]);

  useEffect(() => {
    if (!isLevelerAllowed) return;

    setLevelerStatus((prevState) => ({
      ...prevState,
      levelerDisabled: shouldDisableLeveler({
        language,
        parent,
        gradeBand,
        isOriginalLevel,
        levelAttempts: levelerStatus.levelAttempts,
        text
      }),
    }));

    if (levelerStatus.leveledText !== text) {
      setLevelerStatus((prevState) => ({
        ...prevState,
        levelAttempts: 0
      }));
    }
  }, [gradeBand, text, levelerStatus.levelAttempts, levelerStatus.leveledText, isLevelerAllowed]);

  useEffect(() => {
    if (!isLevelerAllowed) return;

    if (leveledData.error) {
      error(leveledData.error, {
        persist: true,
        onClick: () => {
          setLeveledData((prevState) => {
            return {
              ...prevState,
              error: null,
            };
          });
        }

      });
    }
  }, [leveledData.error, isLevelerAllowed]);

  // Exit early if the schema is not allowed to avoid issues with Leveler logic.
  // Hooks must be called unconditionally, so this check must be inside `useLeveler` after its required hooks.
  if (!isLevelerAllowed) {
    return {
      fetchLeveledArticle,
      leveledData,
      levelerStatus,
      setLevelerStatus,
      setLeveledData
    };
  }
  const convertBandToRange = (grade) => {
    // Seti is presently only leveling grades up to 10 and no less than 2.
    const SETI_GRADE_MAPPING = {
      GRADE_12: 'GRADE_10',
      GRADE_11: 'GRADE_10',
      GRADE_1: 'GRADE_2',
      GRADE_K: 'GRADE_2'
    };

    const gradeForSeti = SETI_GRADE_MAPPING[grade] || grade;

    const range = TARGET_LEXILE[gradeForSeti];
    return range && range.length === 2 ? `${range[0]}-${range[1]}L` : null;
  };

  const range = convertBandToRange(gradeBand);

  if (!range) setLeveledData((prev) => ({ ...prev, error: `Can't level the grade band: ${gradeBand}` }));

  return {
    fetchLeveledArticle,
    leveledData,
    levelerStatus,
    setLevelerStatus,
    setLeveledData
  };
};

export default useLeveler;
